import { useState, useRef, useEffect } from 'react';
import { LogOut, User } from 'lucide-react';
import { useInsforge } from '../provider/InsforgeProvider';
import { SignOutButton } from './SignOutButton';
import { UserProfileModal } from './UserProfileModal';
import {
  UserButtonContainer,
  UserButtonButton,
  UserButtonAvatar,
  UserButtonAvatarImage,
  UserButtonAvatarInitials,
  UserButtonInfo,
  UserButtonName,
  UserButtonEmail,
  UserButtonMenu,
  UserButtonMenuItem,
  UserButtonMenuItemIcon,
} from './styled';

export interface UserButtonProps {
  afterSignOutUrl?: string;
  mode?: 'detailed' | 'simple';
  /** Whether to show the Profile menu item (default: true) */
  showProfile?: boolean;
  /** Callback when profile update fails */
  onProfileError?: (error: string) => void;
}

/**
 * User profile button with dropdown menu and sign-out functionality.
 *
 * Styles are powered by Emotion CSS-in-JS to prevent FOUC in SSR environments.
 */
export function UserButton({
  afterSignOutUrl = '/',
  mode = 'simple',
  showProfile = true,
  onProfileError,
}: UserButtonProps) {
  const { user } = useInsforge();
  const [isOpen, setIsOpen] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Validate image URL when avatarUrl changes
  useEffect(() => {
    setImageError(false);

    const avatarUrl = user?.profile?.avatar_url;
    if (!avatarUrl) {
      return;
    }

    const checkImageUrl = async () => {
      try {
        const response = await fetch(avatarUrl, {
          method: 'HEAD',
          cache: 'no-cache',
        });
        if (!response.ok) {
          setImageError(true);
        }
      } catch (error: unknown) {
        if (error instanceof Error) {
          console.error('Error checking image URL:', error.message);
        }
        setImageError(true);
      }
    };

    void checkImageUrl();
  }, [user?.profile?.avatar_url]);

  // Detect position and determine menu placement (vertical and horizontal)
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const buttonRect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Vertical positioning
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      const menuHeight = 200; // Estimate
      const shouldOpenUpward = spaceBelow < menuHeight && spaceAbove > spaceBelow;
      setOpenUpward(shouldOpenUpward);

      // Horizontal positioning
      // We need to wait for next tick to get the menu's actual width
      requestAnimationFrame(() => {
        if (menuRef.current) {
          const menuWidth = menuRef.current.offsetWidth;
          const menuRight = buttonRect.left + menuWidth;
          const viewportPadding = 16; // 16px padding from viewport edge

          // If menu would overflow right edge of viewport
          if (menuRight > viewportWidth - viewportPadding) {
            // Calculate how much to shift left
            const overflow = menuRight - (viewportWidth - viewportPadding);
            setHorizontalOffset(-overflow);
          } else {
            setHorizontalOffset(0);
          }
        }
      });
    } else {
      // Reset when closed
      setHorizontalOffset(0);
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!user) {
    return null;
  }

  const initials = user.profile?.name
    ? user.profile.name.charAt(0).toUpperCase()
    : user.email.split('@')[0].slice(0, 2).toUpperCase();

  return (
    <UserButtonContainer ref={dropdownRef}>
      <UserButtonButton
        $detailed={mode === 'detailed'}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <UserButtonAvatar>
          {user.profile?.avatar_url && !imageError ? (
            <UserButtonAvatarImage
              src={user.profile.avatar_url}
              alt={user.email}
              onError={() => setImageError(true)}
            />
          ) : (
            <UserButtonAvatarInitials>{initials}</UserButtonAvatarInitials>
          )}
        </UserButtonAvatar>
        {mode === 'detailed' && (
          <UserButtonInfo>
            {user.profile?.name && <UserButtonName>{user.profile.name}</UserButtonName>}
            <UserButtonEmail>{user.email}</UserButtonEmail>
          </UserButtonInfo>
        )}
      </UserButtonButton>

      {isOpen && (
        <UserButtonMenu ref={menuRef} $openUpward={openUpward} $horizontalOffset={horizontalOffset}>
          {showProfile && (
            <UserButtonMenuItem
              onClick={() => {
                setShowProfileModal(true);
                setIsOpen(false);
              }}
            >
              <UserButtonMenuItemIcon>
                <User />
              </UserButtonMenuItemIcon>
              Profile
            </UserButtonMenuItem>
          )}
          <SignOutButton afterSignOutUrl={afterSignOutUrl}>
            <UserButtonMenuItem $signout onClick={() => setIsOpen(false)}>
              <UserButtonMenuItemIcon>
                <LogOut />
              </UserButtonMenuItemIcon>
              Sign out
            </UserButtonMenuItem>
          </SignOutButton>
        </UserButtonMenu>
      )}

      {showProfileModal && (
        <UserProfileModal onClose={() => setShowProfileModal(false)} onError={onProfileError} />
      )}
    </UserButtonContainer>
  );
}
